import React, { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useTranslations } from '../translations';
import './InstallAppBanner.css';

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

declare global {
  interface Window {
    __roadeInstallPrompt?: InstallPromptEvent;
  }
}

type InstallMode = 'native' | 'ios';

const DISMISSAL_STORAGE_KEY = 'roade-install-prompt-dismissed-at-v3';
const DISMISSAL_DURATION_MS = 30 * 24 * 60 * 60 * 1000;
const MOBILE_INSTALL_QUERY = '(max-width: 1024px)';

const matchesMediaQuery = (query: string) => (
  typeof window.matchMedia === 'function' && window.matchMedia(query).matches
);

const isStandalone = () => (
  matchesMediaQuery('(display-mode: standalone)')
  || Boolean((navigator as Navigator & { standalone?: boolean }).standalone)
);

const isIosDevice = () => (
  /iPad|iPhone|iPod/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
);

const wasRecentlyDismissed = () => {
  const dismissedAt = Number(localStorage.getItem(DISMISSAL_STORAGE_KEY));
  return Number.isFinite(dismissedAt)
    && dismissedAt > 0
    && Date.now() - dismissedAt < DISMISSAL_DURATION_MS;
};

const InstallAppBanner: React.FC = () => {
  const t = useTranslations();
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    matchesMediaQuery(MOBILE_INSTALL_QUERY)
  ));
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(
    () => window.__roadeInstallPrompt ?? null,
  );
  const [showIosInstructions, setShowIosInstructions] = useState(false);
  const [dismissed, setDismissed] = useState(wasRecentlyDismissed);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (
      Capacitor.isNativePlatform()
      || isStandalone()
      || typeof window.matchMedia !== 'function'
    ) {
      return;
    }

    const mobileQuery = window.matchMedia(MOBILE_INSTALL_QUERY);
    const handleViewportChange = (event: MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);
      setShowIosInstructions(false);
    };

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      const promptEvent = event as InstallPromptEvent;
      window.__roadeInstallPrompt = promptEvent;
      setInstallPrompt(promptEvent);
    };
    const handleInstalled = () => {
      delete window.__roadeInstallPrompt;
      setInstallPrompt(null);
      setHidden(true);
      localStorage.removeItem(DISMISSAL_STORAGE_KEY);
    };

    mobileQuery.addEventListener('change', handleViewportChange);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);
    return () => {
      mobileQuery.removeEventListener('change', handleViewportChange);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const mode: InstallMode | null = (
    isMobileViewport
    && !dismissed
    && !hidden
    && !Capacitor.isNativePlatform()
    && !isStandalone()
  )
    ? (isIosDevice() ? 'ios' : installPrompt ? 'native' : null)
    : null;

  const dismiss = () => {
    localStorage.setItem(DISMISSAL_STORAGE_KEY, Date.now().toString());
    delete window.__roadeInstallPrompt;
    setDismissed(true);
    setInstallPrompt(null);
  };

  const handleInstall = async () => {
    if (mode === 'ios') {
      setShowIosInstructions(true);
      return;
    }
    if (!installPrompt) {
      return;
    }

    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    delete window.__roadeInstallPrompt;
    if (choice.outcome === 'accepted') {
      setHidden(true);
    } else {
      dismiss();
    }
    setInstallPrompt(null);
  };

  if (!mode) {
    return null;
  }

  return (
    <aside className="install-app-banner" aria-label={t.installApp.title}>
      <div className="install-app-banner-icon" aria-hidden="true">
        {mode === 'ios' ? <Share /> : <Download />}
      </div>
      <div className="install-app-banner-copy">
        <strong>{t.installApp.title}</strong>
        <span>
          {showIosInstructions
            ? t.installApp.iosInstructions
            : t.installApp.description}
        </span>
      </div>
      {!showIosInstructions && (
        <button type="button" className="install-app-banner-action" onClick={handleInstall}>
          {mode === 'ios' ? t.installApp.iosAction : t.installApp.install}
        </button>
      )}
      <button
        type="button"
        className="install-app-banner-dismiss"
        onClick={dismiss}
        aria-label={t.installApp.dismiss}
        title={t.installApp.dismiss}
      >
        <X aria-hidden="true" />
      </button>
    </aside>
  );
};

export default InstallAppBanner;
